import React, { useEffect, useState } from 'react';
import { Order } from '../../types/order';
import { orderService } from '../../services/api';
import { buildOrderCompletionPdf, OrderCompletionPdfData } from './orderCompletionPdf';
import styles from './OrderTrackingModal.module.css';

interface AuditLog {
  id: number;
  action: string;
  message: string;
  performedBy: string;
  performedAt: string;
}

interface OrderTrackingModalProps {
  order: Order;
  onClose: () => void;
}

const actionLabels: Record<string, string> = {
  ABERTURA_OS: 'Abertura da OS',
  COMPROVANTE_IMPRESSO: 'Comprovante Impresso',
  COMPROVANTE_NAO_IMPRESSO: 'Comprovante Não Impresso',
  COMPROVANTE_REIMPRESSO: 'Reimpressão de Comprovante',
  PROTOCOLO_RETROATIVO: 'Protocolo Retroativo',
  PENDENCIA_MOTIVO: 'Pendência',
  CONCLUSAO_OS: 'Conclusão da OS',
  CONCLUSAO_TECNICOS: 'Técnicos Responsáveis',
  OBSERVACOES_TECNICO: 'Observações do Técnico',
};

const actionIcons: Record<string, string> = {
  ABERTURA_OS: '📋',
  COMPROVANTE_IMPRESSO: '🖨️',
  COMPROVANTE_NAO_IMPRESSO: '❌',
  COMPROVANTE_REIMPRESSO: '🔄',
  PROTOCOLO_RETROATIVO: '🔙',
  PENDENCIA_MOTIVO: '⏸️',
  CONCLUSAO_OS: '✅',
  CONCLUSAO_TECNICOS: '👥',
  OBSERVACOES_TECNICO: '📝',
};

const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const extractOperatorName = (message: string, performedBy: string): string => {
  const match = message.match(/Usuario \[([^\]]+)\]/);
  if (match && match[1]) {
    return match[1];
  }
  return performedBy;
};

const OrderTrackingModal: React.FC<OrderTrackingModalProps> = ({ order, onClose }) => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const data = await orderService.getAuditLogs(order.id);
        setLogs(data);
      } catch {
        setError('Erro ao carregar histórico');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [order.id]);

  const checklist = order.completionChecklist;
  const isConcluded = order.status === 'CONCLUIDA';

  const handlePrintCompletion = async () => {
    setPrinting(true);
    try {
      const data: OrderCompletionPdfData = {
        protocolNumber: order.protocolNumber,
        address: order.address,
        neighborhood: order.neighborhood,
        reporterName: order.reporterName,
        houseNumberInFront: order.houseNumberInFront,
        referencePoint: order.referencePoint,
        poleBusbar: order.poleBusbar,
        description: order.description,
        priority: order.priority,
        createdAt: order.createdAt,
        updatedAt: order.updatedAt,
        checklist: order.completionChecklist,
      };

      const document = await buildOrderCompletionPdf(data);
      const blobUrl = URL.createObjectURL(document.output('blob'));
      const printWindow = window.open(blobUrl, '_blank');

      if (!printWindow) {
        URL.revokeObjectURL(blobUrl);
        alert('Não foi possível abrir a janela de impressão. Verifique se o navegador bloqueou pop-ups.');
        return;
      }

      printWindow.addEventListener('load', () => {
        setTimeout(() => printWindow.print(), 150);
      });
      printWindow.addEventListener('afterprint', () => URL.revokeObjectURL(blobUrl));
      printWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl));
    } catch {
      alert('Erro ao gerar o comprovante de conclusão.');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <h3 className={styles.title}>Acompanhamento da OS</h3>
            {order.protocolNumber && (
              <span className={styles.protocol}>Protocolo: {order.protocolNumber}</span>
            )}
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className={styles.body}>
          {isConcluded && checklist && (
            <div className={styles.checklistSection}>
              <h4 className={styles.checklistTitle}>Serviço realizado</h4>

              <div className={styles.checklistGrid}>
                <div className={styles.checklistItem}>
                  <span className={styles.checklistLabel}>Lâmpada encontrada</span>
                  <span className={styles.checklistValue}>{checklist.lampadaEncontrada || '-'}</span>
                </div>
                <div className={styles.checklistItem}>
                  <span className={styles.checklistLabel}>Lâmpada instalada</span>
                  <span className={styles.checklistValue}>{checklist.lampadaInstalada || '-'}</span>
                </div>
                <div className={styles.checklistItem}>
                  <span className={styles.checklistLabel}>Refletor instalado</span>
                  <span className={styles.checklistValue}>{checklist.refletorInstalado || '-'}</span>
                </div>
              </div>

              <div className={styles.materialsBlock}>
                <span className={styles.checklistLabel}>Materiais utilizados</span>
                <div className={styles.materialsList}>
                  {(checklist.materiaisUtilizados ?? []).length === 0 ? (
                    <span className={styles.checklistValue}>-</span>
                  ) : (
                    (checklist.materiaisUtilizados ?? []).map((material) => (
                      <span key={material} className={styles.materialChip}>
                        {material} ×{checklist.materiaisQtd?.[material] ?? 1}
                      </span>
                    ))
                  )}
                </div>
              </div>

              {checklist.observacoes?.trim() && (
                <div className={styles.materialsBlock}>
                  <span className={styles.checklistLabel}>Observações</span>
                  <span className={styles.checklistValue}>{checklist.observacoes}</span>
                </div>
              )}

              {(checklist.tecnicos?.length ?? 0) > 0 && (
                <div className={styles.materialsBlock}>
                  <span className={styles.checklistLabel}>Técnicos responsáveis</span>
                  <span className={styles.checklistValue}>{checklist.tecnicos?.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          <h4 className={styles.historyTitle}>Histórico de acompanhamento</h4>

          {loading && <p className={styles.loading}>Carregando histórico...</p>}
          {error && <p className={styles.error}>{error}</p>}

          {!loading && !error && logs.length === 0 && (
            <div className={styles.emptyState}>
              <p>Nenhum registro de acompanhamento encontrado.</p>
            </div>
          )}

          {!loading && !error && logs.length > 0 && (
            <div className={styles.timeline}>
              {logs.map((log, index) => (
                <div key={log.id} className={styles.timelineItem}>
                  <div className={styles.timelineDot} />
                  {index < logs.length - 1 && <div className={styles.timelineLine} />}
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineHeader}>
                      <span className={styles.timelineIcon}>
                        {actionIcons[log.action] || '📌'}
                      </span>
                      <span className={styles.timelineAction}>
                        {actionLabels[log.action] || log.action}
                      </span>
                    </div>
                    <p className={`${styles.timelineMessage} ${log.action === 'PENDENCIA_MOTIVO' ? styles.timelineMessageBold : ''}`}>{log.message}</p>
                    <div className={styles.timelineFooter}>
                      <span className={styles.timelineUser}>{extractOperatorName(log.message, log.performedBy)}</span>
                      <span className={styles.timelineDate}>{formatDateTime(log.performedAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {isConcluded && (
            <button
              className={styles.printButton}
              onClick={handlePrintCompletion}
              disabled={printing}
            >
              {printing ? 'Gerando...' : '🖨️ Imprimir comprovante de conclusão'}
            </button>
          )}
          <button className={styles.closeButton} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingModal;
